import type { ApiRegisterPayload } from 'src/types/userAuth';
import type { ApiLocationCity, ApiLocationCountry } from 'src/types/location';

import { AccountRole } from 'src/types/userAuth';

import { isValidEmail } from './device';

/**
 * The cities a dealer can choose from: those of the chosen country, as the list has them now. None
 * before a country is chosen, and none for a code the list does not carry. The lookup is by the ISO
 * code the form holds, never by the name the list shows.
 */
export const citiesOf = (countries: ApiLocationCountry[], countryCode: string): ApiLocationCity[] =>
  countries.find((country) => country.countryCode === countryCode)?.cities ?? [];

/**
 * The city that counts: the chosen `locationId`, but only while it is one of `cities`. A city left
 * over from the country chosen before, or switched off while the form was open, is nobody's choice
 * and never reaches the api — whatever the form's state still holds.
 */
export const chosenCityId = (cities: ApiLocationCity[], locationId: string): string =>
  cities.some((city) => city.id === locationId) ? locationId : '';

/** What the registration form holds when Continue is pressed. */
export interface RegisterFormValues {
  email: string;
  role: AccountRole;
  name: string;
  /** The dealer's city, an id from GET /public/locations; empty until chosen. */
  locationId: string;
  marketingConsent: boolean;
  /** The dealer's early-access checkbox. */
  dealerTerms: boolean;
}

/**
 * Why the form cannot be sent yet, or `null` when it can (api#298, partners-spec §3.8). A dealer
 * names a country and a city — the field is required for them and does not exist for a buyer
 * (decision no. 13), so only the dealer plan can be stopped by it. The email is judged as it will
 * be sent: trimmed, so a space a keyboard appended does not refuse a good address.
 */
export const registerFormError = (values: RegisterFormValues): string | null => {
  if (!isValidEmail(values.email.trim())) {
    return 'Enter a valid email address.';
  }

  if (values.role === AccountRole.DEALER && !values.locationId) {
    return 'Choose your country and city.';
  }

  if (values.role === AccountRole.DEALER && !values.dealerTerms) {
    return 'Please accept the early-access terms to continue.';
  }

  return null;
};

/**
 * The body of `POST /user/auth/register`. The dealer's place travels as `locationId`; nothing is
 * sent for a buyer, who has no region (and the free-text `region` of the old form is gone for both).
 */
export const registerPayload = (values: RegisterFormValues): ApiRegisterPayload => ({
  email: values.email.trim(),
  role: values.role,
  name: values.name.trim() || undefined,
  locationId: values.role === AccountRole.DEALER && values.locationId ? values.locationId : undefined,
  marketingConsent: values.marketingConsent,
});
